import {
  AccessToken,
  AuthContainer,
  AuthenticatorProvider,
  AuthProvider,
  isDocumentNavigation,
  type MiddlewareFactory,
  useSecureCookies,
} from "@pharaday/auth";
import { POSTHOG_KEY } from "@pharaday/env";
import { type NextFetchEvent, type NextRequest } from "next/server";
import { v4 as uuidv4 } from "uuid";

import { PostHogServer } from "../../../utils/posthog-server";
import { FeatureFlagService, type PostHogBootstrap } from "../../application/feature-flag.service";
import type { FeatureFlagEnum } from "../../domain/feature-flag.enum";
import { FeatureFlagContainer } from "../../feature-flag.container";

export const COOKIE_BOOTSTRAP_POSTHOG = "bootstrapPostHog";

export class PosthogFeatureFlagService extends FeatureFlagService {
  private parseBootstrapCookie(raw: string | undefined): Partial<PostHogBootstrap> | undefined {
    if (!raw) {
      return undefined;
    }
    try {
      const parsed: unknown = JSON.parse(raw);

      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        return undefined;
      }

      const bootstrap = parsed as Partial<PostHogBootstrap>;

      if (bootstrap.distinctID !== undefined && typeof bootstrap.distinctID !== "string") {
        return undefined;
      }

      return bootstrap;
    } catch {
      return undefined;
    }
  }

  // The client only reads the bootstrap cookie on document load (posthog.init / FeatureFlagProvider
  // mount), so router fetches (prefetches and soft navigations — indistinguishable in middleware, see
  // isDocumentNavigation) never need a refresh — unless the cookie is unusable or the auth state
  // changed since it was written (login/logout/user switch must re-target flags before the provider
  // next mounts).
  private shouldRefreshBootstrap(
    request: NextRequest,
    bootstrap: Partial<PostHogBootstrap> | undefined,
    id: string | undefined
  ): boolean {
    if (isDocumentNavigation(request)) {
      return true;
    }
    if (!bootstrap?.distinctID || bootstrap.featureFlags === undefined) {
      return true;
    }
    if (Boolean(id) !== Boolean(bootstrap.isIdentifiedID)) {
      return true;
    }
    return id !== undefined && bootstrap.distinctID !== id;
  }

  withFeatureFlagMiddleware: MiddlewareFactory = next => {
    return async (request: NextRequest, event: NextFetchEvent) => {
      const secure = useSecureCookies(request);

      const session = await AuthContainer.get(AuthProvider).getSession();
      const accessToken = session?.accessToken && new AccessToken(session.accessToken.value);
      const id =
        accessToken && !accessToken?.isExpired()
          ? AuthContainer.get(AuthenticatorProvider).extractAccessToken(accessToken).id
          : undefined;

      const bootstrap = this.parseBootstrapCookie(request.cookies.get(COOKIE_BOOTSTRAP_POSTHOG)?.value);

      // Router fetches (prefetches and soft navigations) with a usable, identity-matching cookie
      // never touch PostHog and never rewrite the cookie.
      if (!this.shouldRefreshBootstrap(request, bootstrap, id)) {
        return next(request, event);
      }

      const distinctID = id ?? bootstrap?.distinctID ?? uuidv4();
      const isIdentifiedID = Boolean(id);

      const requestOptions = {
        method: "POST",
        headers: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          api_key: FeatureFlagContainer.get(POSTHOG_KEY),
          distinct_id: distinctID,
        }),
      };

      const decidePromise = fetch("https://eu.posthog.com/decide?v=3", requestOptions)
        .then(result => {
          if (!result.ok) {
            throw new Error(`decide responded ${result.status}`);
          }
          return result.json();
        })
        .then(json => json.featureFlags)
        .catch(error => {
          console.error("🚀 ~ PosthogFeatureFlagService ~ withFeatureFlagMiddleware ~ error:", error);
          return undefined;
        });

      const response = await next(request, event);
      const featureFlags = await decidePromise;

      if (featureFlags !== undefined) {
        const bootstrap: PostHogBootstrap = {
          featureFlags,
          distinctID,
          isIdentifiedID,
        };

        response.cookies.set(COOKIE_BOOTSTRAP_POSTHOG, JSON.stringify(bootstrap), {
          // NOTE: Not enable httpOnly because PostHog client need to read this data to bootstrap its feature flags.
          // httpOnly: true,
          secure,
          sameSite: "lax",
          path: "/",
        });
      }

      return response;
    };
  };

  async isFeatureFlagEnabled(flag: FeatureFlagEnum, id: string) {
    const postHog = await PostHogServer();
    const isEnabled = Boolean(await postHog.isFeatureEnabled(flag, id));
    await postHog.shutdown();
    return isEnabled;
  }
}
